import {
    getSelectedTableView,
    getListViewState,
    isItemPartOperation,
    MailRowDataPropertyGetter,
} from 'owa-mail-list-store';
import getEffectiveRowKeys from 'owa-mail-list-store/lib/utils/getEffectiveRowKeys';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import type FlagStatus from 'owa-service/lib/contract/FlagStatus';
import { assertNever } from 'owa-assert';
import type { ClientItemId } from 'owa-client-ids';
import type { ClientItem } from 'owa-mail-store';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';

/**
 * Returns a map that determines whether to enable or disable MarkComplete and ClearFlag.
 * Enable logic is:
 *     - Mark Complete: Enabled if any items are marked 'Flagged' or 'NotFlagged'
 *     - Clear Flag: Enabled if any items are marked 'Flagged' or 'Complete'
 * @param itemId optional item id to inspect a specific item; when omitted the current selection is used
 */
export function getFlagUnflagEnableMap(itemId: ClientItemId | undefined): {
    shouldEnableMarkComplete: boolean;
    shouldEnableClearFlag: boolean;
} {
    const enableMap = {
        shouldEnableMarkComplete: false,
        shouldEnableClearFlag: false,
    };

    if (itemId) {
        const item: ClientItem | undefined = getMailStore().items.get(itemId.Id);
        updateEnableMap(item?.Flag?.FlagStatus, enableMap);
    } else if (isItemPartOperation()) {
        const selectedNodeIds = getListViewState().expandedConversationViewState.selectedNodeIds;
        for (const selectedItemId of selectedNodeIds) {
            const item = getItemToShowFromNodeId(selectedItemId);
            if (item) {
                updateEnableMap(item.Flag?.FlagStatus, enableMap);

                // Optimization: if all items in enableMap are enabled, then no need to continue loop.
                if (
                    enableMap.shouldEnableMarkComplete === true &&
                    enableMap.shouldEnableClearFlag === true
                ) {
                    break;
                }
            }
        }
    } else {
        const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
        if (!tableView) {
            return enableMap;
        }
        const rowKeys = tableView.isInVirtualSelectAllMode
            ? tableView.rowKeys
            : getEffectiveRowKeys(tableView);
        for (const rowKey of rowKeys) {
            updateEnableMap(MailRowDataPropertyGetter.getFlagStatus(rowKey, tableView), enableMap);

            // Optimization: if all items in enableMap are enabled, then no need to continue loop.
            if (
                enableMap.shouldEnableMarkComplete === true &&
                enableMap.shouldEnableClearFlag === true
            ) {
                break;
            }
        }
    }

    return enableMap;
}

/**
 * Internal function to determine whether to enable or disable menu items.
 * @param flagStatus current item's flag status
 * @param enableMap object of booleans to update given flagStatus
 */
function updateEnableMap(
    flagStatus: FlagStatus | undefined | null,
    enableMap: {
        shouldEnableMarkComplete: boolean;
        shouldEnableClearFlag: boolean;
    }
) {
    switch (flagStatus) {
        case 'Flagged':
            enableMap.shouldEnableMarkComplete = true;
            enableMap.shouldEnableClearFlag = true;
            break;
        case 'NotFlagged':
            enableMap.shouldEnableMarkComplete = true;
            break;
        case 'Complete':
            enableMap.shouldEnableClearFlag = true;
            break;
        case undefined:
        case null:
            // Case `undefined` or `null` is when an item has never been flagged or unflagged before. It acts as "NotFlagged".
            enableMap.shouldEnableMarkComplete = true;
            break;
        default:
            assertNever(flagStatus);
    }
}
